import React from 'react';
import { mimeTypeToAppType } from 'utils/file/MIMETypeUtils';
import { ItemAvatar } from '../components/ItemAvatar';
import { ItemIcon } from '../components/ItemIcon';
import {
  useItemHandleFragment,
  useItemHandleFragmentProps,
} from './useItemHandleFragment';
import { useLazyLoadItemHandleQuery } from './useItemHandleQuery';
import { useItemLinkProps } from './useItemLinkProps';
import { useItemName } from './useItemName';
import { useItemThumbnailSrc } from './useItemThumbnailSrc';
import { useItemTypeDescription } from './useItemTypeDescription';
import { usePublishDate } from './usePublishDate';
import { usePublisherName } from './usePublisherName';
import { useLastModifiedDate } from './useLastModifiedData';

export type { useItemHandleFragmentProps } from './useItemHandleFragment';

export const useItem = ({ item: itemRef }: useItemHandleFragmentProps) => {
  const item = useItemHandleFragment({ item: itemRef });
  return {
    id: item.id,
    type: item.__typename,
    contentType: item.content?.__typename,
    contentData:
      item.content?.__typename === 'Page' ? item.content.data : undefined,
    mimeType:
      item.content?.__typename == 'File' ? item.content.mimeType : undefined,
    fileType:
      item.content?.__typename == 'File'
        ? mimeTypeToAppType(item.content.mimeType)
        : undefined,
    signatureEnvelope: item.signatureEnvelope,
    icon: <ItemIcon item={item} />,
    linkProps: useItemLinkProps(item),
    thumbnailSrc: useItemThumbnailSrc(item),
    newThumbnailSrc: item.thumbnail?.url,
    publishDate: usePublishDate(item),
    lastModifiedDate: useLastModifiedDate(item),
    description: useItemTypeDescription(item),
    publisherName: usePublisherName(item),
    name: useItemName(item),
    avatar: <ItemAvatar item={item} />,
    questions:
      item.content?.__typename === 'Form' ? item.content.questions : undefined,
  };
};

export const useItemById = ({ itemId }: { itemId: string }) => {
  const { item: itemRef } = useLazyLoadItemHandleQuery({ itemId });
  if (!itemRef) {
    throw new Error(`Could not get item ${itemId}`);
  }
  const item = useItemHandleFragment({ item: itemRef });

  return {
    id: item.id,
    type: item.__typename,
    contentType: item.content?.__typename,
    mimeType:
      item.content?.__typename == 'File' ? item.content.mimeType : undefined,
    fileType:
      item.content?.__typename == 'File'
        ? mimeTypeToAppType(item.content.mimeType)
        : undefined,
    signatureEnvelope: item.signatureEnvelope,
    icon: <ItemIcon item={item} />,
    linkProps: useItemLinkProps(item),
    thumbnailSrc: useItemThumbnailSrc(item),
    newThumbnailSrc: item.thumbnail?.url,
    publishDate: usePublishDate(item),
    description: useItemTypeDescription(item),
    publisherName: usePublisherName(item),
    name: useItemName(item),
    avatar: <ItemAvatar item={item} />,
  };
};
